import { BookOpen, Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  adminRefreshProjects,
  type GitHubConnectionStatus,
  type OutOfSyncProject,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { useUser } from "@/lib/user-context";

import { GitHubConnectBanner } from "./github-connect-banner";
import { NewProjectDialog } from "./new-project-dialog";
import { OrgAccessWarning } from "./org-access-warning";
import { OutOfSyncDialog } from "./out-of-sync-dialog";
import { ProjectList } from "./project-list";

const SYNC_POLL_MIN_MS = 2 * 60_000; // 2 minutes
const SYNC_POLL_MAX_MS = 3 * 60_000; // 3 minutes

export function HomePage() {
  const { user } = useUser();
  const [githubStatus, setGithubStatus] = useState<GitHubConnectionStatus | null>(null);
  const githubConnected = githubStatus?.connected ?? false;
  // Treat null/undefined as "unknown" rather than "no access" -- only a
  // confirmed `false` from the backend should block the UI. The server-side
  // check on POST /projects is the source of truth for actual creation.
  const orgAccessKnownDenied = githubStatus?.has_org_access === false;
  const needsReconnect = githubStatus?.needs_reconnect === true;
  // GitHub blocks all org access for accounts that fail the org's secure-2FA
  // policy (e.g. SMS still enabled). Gate creation -- the banner explains how
  // to fix it (reconnecting does not help).
  const twoFactorBlocked = githubStatus?.two_factor_blocked === true;
  // Project creation requires both a valid connection and verifiable
  // membership. If we cannot verify (needs reconnect), we gate creation
  // until the user reconnects -- their banner explains how.
  const hasOrgAccess =
    githubConnected && !orgAccessKnownDenied && !needsReconnect && !twoFactorBlocked;
  // The 2FA block is a more specific concern than a generic org-access denial,
  // so it takes priority -- the GitHub banner explains it. Don't also show the
  // generic org-access warning in that case.
  const showOrgWarning = githubConnected && orgAccessKnownDenied && !twoFactorBlocked;
  // A keyless instance (no GitHub OAuth app mounted, NOR-467) can't scaffold
  // anything even for a user whose stored connection still checks out -- the
  // banner says so, and the New Project button must agree with it.
  const oauthConfigured = githubStatus?.oauth_configured !== false;
  const canCreateProject = githubConnected && hasOrgAccess && oauthConfigured;
  const [refreshKey, setRefreshKey] = useState(0);
  const [outOfSync, setOutOfSync] = useState<OutOfSyncProject[]>([]);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlProjectId = searchParams.get("project")
    ? Number(searchParams.get("project"))
    : undefined;
  const urlTab = searchParams.get("tab") ?? undefined;
  const isNewOpen = searchParams.get("new") === "true";

  const openProject = useCallback(
    (id: number, initialTab?: string) => {
      const params = new URLSearchParams({ project: String(id) });
      if (initialTab) {
        params.set("tab", initialTab);
      }
      navigate(`/home?${params.toString()}`);
    },
    [navigate],
  );

  const closeProject = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  const setProjectTab = useCallback(
    (newTab: string) => {
      if (!urlProjectId) {
        return;
      }
      const params = new URLSearchParams({ project: String(urlProjectId), tab: newTab });
      navigate(`/home?${params.toString()}`, { replace: true });
    },
    [navigate, urlProjectId],
  );

  const openNew = useCallback(() => {
    navigate("/home?new=true");
  }, [navigate]);

  const closeNew = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  const isAdmin = user?.role === "admin";
  const greeting = user?.first_name ? `Welcome, ${user.first_name}` : "Welcome";

  const handleGithubStatusChange = useCallback((status: GitHubConnectionStatus | null) => {
    setGithubStatus(status);
  }, []);

  const handleProjectCreated = useCallback(() => {
    closeNew();
    setRefreshKey((k) => k + 1);
  }, [closeNew]);

  const handleSyncResolved = useCallback(() => {
    setSyncDialogOpen(false);
    setOutOfSync([]);
    setRefreshKey((k) => k + 1);
  }, []);

  // Background polling for admins: check for out-of-sync projects on mount
  // and every 2-3 minutes (jittered). Uses `background=true` so the backend
  // skips activity logging for these automated checks.
  const syncDialogOpenRef = useRef(syncDialogOpen);
  useEffect(() => {
    syncDialogOpenRef.current = syncDialogOpen;
  });

  useEffect(() => {
    if (!isAdmin || !githubConnected || !hasOrgAccess || !oauthConfigured) {
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    let active = true;

    const run = async () => {
      if (!active || syncDialogOpenRef.current) {
        return;
      }
      try {
        const { data: result } = await adminRefreshProjects({ query: { background: true } });
        const outOfSyncProjects = result?.out_of_sync ?? [];
        if (outOfSyncProjects.length > 0 && !syncDialogOpenRef.current && active) {
          setOutOfSync(outOfSyncProjects);
          setSyncDialogOpen(true);
        }
      } catch {
        logger.debug("[sync-poll] background check failed");
      }
    };

    const schedule = () => {
      const jitter = SYNC_POLL_MIN_MS + Math.random() * (SYNC_POLL_MAX_MS - SYNC_POLL_MIN_MS);
      timer = setTimeout(async () => {
        if (!active) {
          return;
        }
        await run();
        if (active) {
          schedule();
        }
      }, jitter);
    };

    // Run immediately, then poll with jitter
    run().then(() => {
      if (active) {
        schedule();
      }
    });

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [isAdmin, githubConnected, hasOrgAccess, oauthConfigured]);

  const newProjectButton = (
    <Button disabled={!canCreateProject} size="sm" onClick={openNew}>
      <Plus className="mr-2 size-4" />
      New Project
    </Button>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-primary text-3xl font-normal">{greeting}</h2>
          <p className="text-muted-foreground mt-1 text-sm">Manage your scaffold projects</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/how-to">
            <Button variant="outline" size="sm">
              <BookOpen className="mr-2 size-4" />
              Get Started
            </Button>
          </Link>
          {newProjectButton}
        </div>
      </div>

      <GitHubConnectBanner onStatusChange={handleGithubStatusChange} />

      <div className="mt-8">
        {!oauthConfigured ? null : showOrgWarning ? (
          // A keyless instance (NOR-467) renders nothing below the banner: it
          // can't connect GitHub, so there is neither a connect prompt nor a
          // project list with its management controls to offer.
          <OrgAccessWarning
            username={githubStatus?.github_username ?? null}
            githubOrg={githubStatus?.github_org ?? null}
          />
        ) : (
          <ProjectList
            githubConnected={githubConnected}
            refreshTrigger={refreshKey}
            projectId={urlProjectId}
            tab={urlTab}
            openProject={openProject}
            closeProject={closeProject}
            setProjectTab={setProjectTab}
            newProjectButton={newProjectButton}
          />
        )}
      </div>

      <NewProjectDialog
        open={isNewOpen && canCreateProject}
        onOpenChange={(open) => (open ? openNew() : closeNew())}
        onProjectCreated={handleProjectCreated}
      />

      <OutOfSyncDialog
        projects={outOfSync}
        open={syncDialogOpen}
        onOpenChange={setSyncDialogOpen}
        onResolved={handleSyncResolved}
      />
    </div>
  );
}
